import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Loader2,
  BadgeCheck,
  Star,
  Heart,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useFeaturedListings } from "@/hooks/use-listings";
import { optimizeImageUrl } from "@/lib/image-utils";
import SectionHeading from "./SectionHeading";
import type { Listing } from "@/lib/types";

function cardTag(spa: Listing): string {
  if (spa.is_featured) return "Featured";
  if (spa.rating >= 4.8) return "Top Rated";
  return "Verified";
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < Math.round(rating)
              ? "fill-amber-400 text-amber-400"
              : "text-muted-foreground/30"
          }`}
        />
      ))}
    </div>
  );
}

function SpaCard({ spa }: { spa: Listing }) {
  const location = [spa.city, spa.state].filter(Boolean).join(", ");
  const photo = spa.image_url || spa.logo_url;

  return (
    <Link
      to={`/spa/${spa.slug}`}
      className="group transform-gpu rounded-3xl border border-white/10 bg-gradient-to-b from-[#0f2531] to-[#07121b] flex flex-col overflow-hidden w-full h-full hover:-translate-y-1.5 hover:border-primary/30 transition-all duration-300"
    >
      {/* Photo */}
      <div className="relative h-48 bg-secondary overflow-hidden rounded-t-3xl">
        {photo ? (
          <img
            src={optimizeImageUrl(photo, 600)}
            alt={`${spa.name} MedSpa${spa.city ? ` in ${spa.city}` : ""}${spa.state ? `, ${spa.state}` : ""}`}
            width={600}
            height={384}
            loading="lazy"
            className="w-full h-full object-cover transform-gpu group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/15 to-accent/10 text-primary/50">
            <span className="text-5xl font-display font-bold">
              {spa.name.charAt(0)}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
        <span className="absolute left-4 top-4 px-3 py-1.5 rounded-full bg-black/70 border border-white/15 text-[11px] font-bold text-teal-300">
          {cardTag(spa)}
        </span>
        <span className="absolute right-4 top-4 w-9 h-9 grid place-items-center rounded-xl bg-black/70 border border-white/15 text-white/80">
          <Heart className="w-4 h-4" />
        </span>
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 mb-1.5">
          <h3 className="font-serif-display text-xl font-bold text-foreground group-hover:text-primary transition-colors truncate">
            {spa.name}
          </h3>
          {spa.tier !== "free" && (
            <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
          )}
        </div>

        {location && (
          <div className="text-sm text-muted-foreground">{location}</div>
        )}

        <div className="mt-5 pt-4 border-t border-white/10 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <StarRating rating={spa.rating} />
            <span className="text-sm text-foreground font-semibold">
              {spa.rating ? spa.rating.toFixed(1) : "—"}
            </span>
          </div>
          {spa.review_count > 0 && (
            <span className="text-xs font-semibold text-teal-200 bg-primary/10 border border-primary/20 rounded-full px-2.5 py-1">
              {spa.review_count} reviews
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function FeaturedSpas() {
  const { data: listings, isLoading } = useFeaturedListings();
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = 360;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <section className="py-16 lg:py-24">
      <div className="container mx-auto px-4 lg:px-8">
        <SectionHeading
          eyebrow="Featured MedSpas"
          title={<>Top Rated MedSpas Near You</>}
          description="Verified, highly-rated providers worth paying attention to"
          action={
            <div className="hidden md:flex items-center gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => scroll("left")}
                  className="p-2.5 rounded-xl border border-white/10 bg-white/5 text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                  aria-label="Scroll left"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scroll("right")}
                  className="p-2.5 rounded-xl border border-white/10 bg-white/5 text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
                  aria-label="Scroll right"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <Link
                to="/browse"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/10 bg-white/5 text-sm font-semibold text-foreground hover:border-primary/30 transition-colors"
              >
                View all listings
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          }
        />

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          </div>
        ) : listings && listings.length > 0 ? (
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scrollbar-hide pt-2 pb-2 snap-x snap-mandatory"
          >
            {listings.map((spa) => (
              <div
                key={spa.id}
                className="snap-start shrink-0 w-[280px] sm:w-[320px]"
              >
                <SpaCard spa={spa} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-12">
            No listings found yet. Check back soon!
          </p>
        )}

        <div className="mt-8 text-center md:hidden">
          <Link
            to="/browse"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-80 transition-opacity"
          >
            View all med spas
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
